import React, { useMemo } from 'react';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const COLORS30 = [
  '#3366CC',
  '#DC3912',
  '#FF9900',
  '#109618',
  '#990099',
  '#0099C6',
  '#DD4477',
  '#66AA00',
  '#B82E2E',
  '#316395',
  '#994499',
  '#22AA99',
  '#AAAA11',
  '#6633CC',
  '#E67300',
  '#8B0707',
  '#329262',
  '#5574A6',
  '#3B3EAC',
  '#7F7F7F',
  '#8DD3C7',
  '#FFFFB3',
  '#BEBADA',
  '#FB8072',
  '#80B1D3',
  '#FDB462',
  '#B3DE69',
  '#FCCDE5',
  '#D9D9D9',
  '#BC80BD'
];

const withBorders = (colors, borderColor = '#fff', borderWidth = 1) => ({
  backgroundColor: colors,
  borderColor,
  borderWidth
});

const truncate = (str = '', max = 30) => (str.length > max ? str.slice(0, max - 1) + '…' : str);

const PropertyPieChart = ({
  data = { labels: [], datasets: [] },
  loading = false,
  minPctToLabel = 2, // seuil basé sur les % BACKEND
  cutout = '55%'
}) => {
  const ds = data?.datasets?.[0] ?? {};
  const labels = data?.labels ?? [];
  const values = (ds.data ?? []).map(Number);

  // % fournis par le backend
  // - percentages: précision complète (ex: 12.3456)
  // - percentages_display: arrondi pour l’UI (ex: 12.3)
  const pct = (ds.percentages ?? []).map(Number);
  const pctDisplay = (ds.percentages_display ?? pct).map((v) =>
    typeof v === 'number' && !Number.isNaN(v) ? v : 0
  );

  // Helpers d'accès
  const pctAt = (i) => pct[i] ?? 0;
  const pctDispAt = (i) => pctDisplay[i] ?? Math.round(pctAt(i) * 10) / 10;

  const colors = useMemo(() => COLORS30.slice(0, values.length), [values.length]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [{ ...ds, ...withBorders(colors) }]
    }),
    [labels, ds, colors]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout,
      layout: {
        padding: {
          top: 20, // espace supplémentaire en haut
          right: 8,
          bottom: 8,
          left: 8
        }
      },

      plugins: {
        legend: {
          position: 'left',
          labels: { boxWidth: 12, usePointStyle: true }
        },
        tooltip: {
          callbacks: {
            // Utilise les % BACKEND dans le tooltip
            label: (ctx) => {
              const v = Number(ctx.parsed) || 0;
              const p = pctDispAt(ctx.dataIndex);
              return `${ctx.label}: ${v} (${p}%)`;
            }
          }
        },
        datalabels: {
          // Affiche uniquement si le % BACKEND dépasse le seuil
          display: (ctx) => pctAt(ctx.dataIndex) >= minPctToLabel,
          // Label + % (arrondi backend si dispo)
          formatter: (_, ctx) => {
            const label = ctx.chart?.data?.labels?.[ctx.dataIndex] ?? '';
            const p = pctDispAt(ctx.dataIndex);
            return `${truncate(label)} (${p}%)`;
          },
          anchor: 'end',
          align: 'end',
          offset: 6,
          clamp: true,
          color: '#000',
          font: { weight: 500, size: 11 },
          padding: 2
        }
      }
    }),
    [pctAt, pctDispAt, minPctToLabel, cutout]
  );

  return (
    <div className="h-full w-full">
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <ProgressSpinner size="large" />
        </div>
      ) : (
        <Chart
          type="doughnut"
          data={chartData}
          options={chartOptions}
          plugins={[ChartDataLabels]}
          className="h-[calc(100vh-10rem)] w-full"
        />
      )}
    </div>
  );
};

export default PropertyPieChart;
